import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "../ui/button";
import { toast } from "../ui/sonner";
import { ClipboardCheck, CheckCircle2, Circle, Loader2, Undo2, ExternalLink } from "lucide-react";

const fmtAt = (iso) => { try { return iso ? new Date(iso).toLocaleString("zh-CN", { hour12: false, month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" }) : ""; } catch { return ""; } };

const ITEMS = {
  onsign: { zh: "开启「签约即开首期票」", en: "Enable auto first invoice on sign", to: "/finance/funds?tab=ar" },
  recheck: { zh: "运行发票编号一键复检", en: "Run invoice number re-check", to: null },
  sources: { zh: "确认联网检索来源与 OSM 联系邮箱", en: "Confirm research sources & OSM contact", to: null },
  queue: { zh: "核对首期开票队列历史行（线下开票 / 一键开票）", en: "Clear legacy rows in first-invoice queue", to: "/finance/funds?tab=ar" },
};

function autoText(it, zh) {
  const d = it.auto.detail || {};
  switch (it.key) {
    case "onsign": return d.onSign ? (zh ? "开关已开" : "switch on") : (zh ? "开关仍为关" : "switch off");
    case "recheck": return d.lastAt ? (zh ? `最近复检 ${fmtAt(d.lastAt)} · ${d.lastBy || ""} · ${d.canBuildUniqueIndex ? "可建唯一索引" : "存在问题"}` : `last ${fmtAt(d.lastAt)}`) : (zh ? "尚未复检" : "not run yet");
    case "sources": return zh ? `启用 ${d.onCount}/3 · 联系邮箱${d.contactSet ? "已设" : "未设"}${d.updatedAt ? "" : " · 未确认过"}` : `${d.onCount}/3 on · contact ${d.contactSet ? "set" : "missing"}`;
    case "queue": return d.queueCount === 0 ? (zh ? "队列已清空" : "queue empty") : (zh ? `队列尚有 ${d.queueCount} 行` : `${d.queueCount} rows left`);
    default: return "";
  }
}

/* 上线后自检清单: 系统自动检测 + 财务/管理员人工勾选 (记录人 / 时间, 可撤销) */
export function PostPublishChecklist({ api, zh, compact = false }) {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(null);
  const load = useCallback(() => api.get("/admin/post-publish-checklist").then(({ data }) => setD(data)).catch(() => setD(null)), [api]);
  useEffect(() => { load(); }, [load]);
  const tick = async (key, done) => {
    setBusy(key);
    try { const { data } = await api.put(`/admin/post-publish-checklist/${key}`, { done }); setD(data); toast.success(done ? (zh ? "已勾选" : "Checked") : (zh ? "已撤销" : "Unchecked")); }
    catch (e) { toast.error(e?.response?.data?.detail || (zh ? "操作失败" : "Failed")); }
    setBusy(null);
  };
  if (!d) return null;
  if (compact) {
    return (
      <Link to="/admin/pre-publish#post-publish" className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full border ${d.done === d.total ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-violet-200 bg-violet-50 text-violet-700 hover:bg-violet-100"}`} data-testid="post-publish-compact" data-done={d.done} data-total={d.total}>
        <ClipboardCheck className="h-3 w-3" />{zh ? `上线自检 ${d.done}/${d.total}` : `Launch checklist ${d.done}/${d.total}`}<ExternalLink className="h-3 w-3" />
      </Link>
    );
  }
  return (
    <div id="post-publish" className="rounded-xl border border-violet-200 bg-violet-50/40 px-4 py-3 text-xs space-y-2" data-testid="post-publish-checklist" data-done={d.done} data-total={d.total}>
      <div className="flex items-center gap-2 flex-wrap">
        <ClipboardCheck className="h-3.5 w-3.5 text-violet-700" /><b className="text-violet-800">{zh ? "上线后自检清单" : "Post-publish checklist"}</b>
        <span className={`px-2 py-0.5 rounded-full text-[11px] font-bold ${d.done === d.total ? "bg-emerald-100 text-emerald-700" : "bg-violet-100 text-violet-700"}`} data-testid="post-publish-progress">{d.done}/{d.total}</span>
        <span className="text-slate-500">{zh ? `系统自动检测 ${d.autoDone}/${d.total} 项达标 · 人工勾选为最终确认（财务 / 管理员）` : `${d.autoDone}/${d.total} auto-ok · manual tick is final`}</span>
      </div>
      <ul className="space-y-1.5">
        {d.items.map((it) => (
          <li key={it.key} className="flex items-center gap-2 flex-wrap rounded-lg bg-white border border-slate-100 px-3 py-1.5" data-testid={`post-publish-item-${it.key}`} data-done={it.done ? 1 : 0} data-auto={it.auto.ok ? 1 : 0}>
            {it.done ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <Circle className="h-4 w-4 text-slate-300" />}
            <span className={`font-medium ${it.done ? "text-slate-500 line-through" : "text-slate-800"}`}>{zh ? ITEMS[it.key].zh : ITEMS[it.key].en}</span>
            <span className={`px-1.5 py-0.5 rounded text-[10px] ${it.auto.ok ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`} data-testid={`post-publish-auto-${it.key}`}>{zh ? "自动检测：" : "auto: "}{autoText(it, zh)}</span>
            {ITEMS[it.key].to && <Link to={ITEMS[it.key].to} className="text-violet-700 underline decoration-dotted" data-testid={`post-publish-link-${it.key}`}>{zh ? "前往" : "Go"}</Link>}
            <span className="ml-auto text-slate-400" data-testid={`post-publish-by-${it.key}`}>{it.done ? `${it.by || ""} · ${fmtAt(it.at)}` : ""}</span>
            {d.canTick && (
              <Button size="sm" variant={it.done ? "ghost" : "outline"} className="h-6 text-[11px] px-2" disabled={busy === it.key} onClick={() => tick(it.key, !it.done)} data-testid={`post-publish-tick-${it.key}`}>
                {busy === it.key ? <Loader2 className="h-3 w-3 animate-spin" /> : it.done ? <><Undo2 className="h-3 w-3 mr-1" />{zh ? "撤销" : "Undo"}</> : (zh ? "确认完成" : "Confirm")}
              </Button>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
